import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Building2, Plus, QrCode, Edit, Power, Download, RefreshCw, MapPin, Phone, Globe } from 'lucide-react';
import { toast } from 'sonner';
import AreasSection from '@/pages/BranchManagement.AreasSection';
import BranchMapPicker from '@/components/BranchMapPicker';
import {
  BranchAccessFields, EMPTY_BRANCH_ACCESS, branchAccessError, branchAccessFor, saveBranchAccess, useAccessProfiles,
} from '@/components/surveyAccess/BranchAccessFields';

// Country data with states/provinces
const COUNTRIES = [
  { code: 'AE', name: 'United Arab Emirates', phone: '+971', flag: '🇦🇪', states: ['Abu Dhabi','Ajman','Dubai','Fujairah','Ras Al Khaimah','Sharjah','Umm Al Quwain'] },
  { code: 'SA', name: 'Saudi Arabia', phone: '+966', flag: '🇸🇦', states: ['Riyadh','Makkah','Madinah','Eastern Province','Asir','Tabuk','Hail','Northern Borders','Jazan','Najran','Al Bahah','Al Jawf','Qassim'] },
  { code: 'US', name: 'United States', phone: '+1', flag: '🇺🇸', states: ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'] },
  { code: 'GB', name: 'United Kingdom', phone: '+44', flag: '🇬🇧', states: ['England','Scotland','Wales','Northern Ireland'] },
  { code: 'CA', name: 'Canada', phone: '+1', flag: '🇨🇦', states: ['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Nova Scotia','Ontario','Prince Edward Island','Quebec','Saskatchewan'] },
  { code: 'AU', name: 'Australia', phone: '+61', flag: '🇦🇺', states: ['New South Wales','Victoria','Queensland','Western Australia','South Australia','Tasmania','Australian Capital Territory','Northern Territory'] },
  { code: 'IN', name: 'India', phone: '+91', flag: '🇮🇳', states: ['Andhra Pradesh','Delhi','Gujarat','Karnataka','Kerala','Maharashtra','Tamil Nadu','Telangana','Uttar Pradesh','West Bengal','Rajasthan','Punjab','Madhya Pradesh','Bihar','Haryana','Odisha','Jharkhand','Assam','Chhattisgarh','Goa'] },
  { code: 'EG', name: 'Egypt', phone: '+20', flag: '🇪🇬', states: ['Cairo','Alexandria','Giza','Luxor','Aswan','Port Said','Suez','Sharm El Sheikh','Hurghada'] },
  { code: 'QA', name: 'Qatar', phone: '+974', flag: '🇶🇦', states: ['Doha','Al Rayyan','Al Wakrah','Al Khor','Umm Salal','Al Daayen','Al Shamal','Al Sheehaniya'] },
  { code: 'KW', name: 'Kuwait', phone: '+965', flag: '🇰🇼', states: ['Kuwait City','Hawalli','Farwaniya','Ahmadi','Jahra','Mubarak Al-Kabeer'] },
  { code: 'BH', name: 'Bahrain', phone: '+973', flag: '🇧🇭', states: ['Capital','Muharraq','Northern','Southern'] },
  { code: 'OM', name: 'Oman', phone: '+968', flag: '🇴🇲', states: ['Muscat','Dhofar','Musandam','Al Buraimi','Ad Dakhiliyah','Al Batinah North','Al Batinah South','Ash Sharqiyah North','Ash Sharqiyah South','Ad Dhahirah','Al Wusta'] },
  { code: 'JO', name: 'Jordan', phone: '+962', flag: '🇯🇴', states: ['Amman','Zarqa','Irbid','Aqaba','Balqa','Karak','Mafraq','Madaba','Ajloun','Jerash','Tafila','Maan'] },
  { code: 'LB', name: 'Lebanon', phone: '+961', flag: '🇱🇧', states: ['Beirut','Mount Lebanon','North Lebanon','South Lebanon','Beqaa','Nabatieh','Akkar','Baalbek-Hermel'] },
  { code: 'PK', name: 'Pakistan', phone: '+92', flag: '🇵🇰', states: ['Punjab','Sindh','Khyber Pakhtunkhwa','Balochistan','Islamabad'] },
  { code: 'PH', name: 'Philippines', phone: '+63', flag: '🇵🇭', states: ['NCR (Metro Manila)','CAR (Cordillera)','Region I (Ilocos)','Region II (Cagayan Valley)','Region III (Central Luzon)','Region IV-A (CALABARZON)','Region IV-B (MIMAROPA)','Region V (Bicol)','Region VI (Western Visayas)','Region VII (Central Visayas)','Region VIII (Eastern Visayas)','Region IX (Zamboanga Peninsula)','Region X (Northern Mindanao)','Region XI (Davao)','Region XII (SOCCSKSARGEN)','Region XIII (Caraga)','BARMM'] },
  { code: 'MY', name: 'Malaysia', phone: '+60', flag: '🇲🇾', states: ['Kuala Lumpur','Selangor','Johor','Penang','Perak','Sabah','Sarawak','Kedah','Kelantan','Melaka','Negeri Sembilan','Pahang','Perlis','Terengganu'] },
  { code: 'SG', name: 'Singapore', phone: '+65', flag: '🇸🇬', states: ['Singapore'] },
  { code: 'FR', name: 'France', phone: '+33', flag: '🇫🇷', states: ['Île-de-France','Provence-Alpes-Côte d\'Azur','Auvergne-Rhône-Alpes','Nouvelle-Aquitaine','Occitanie','Hauts-de-France','Grand Est','Pays de la Loire','Bretagne','Normandie'] },
  { code: 'DE', name: 'Germany', phone: '+49', flag: '🇩🇪', states: ['Bavaria','Berlin','Hamburg','Hesse','North Rhine-Westphalia','Baden-Württemberg','Lower Saxony','Saxony','Thuringia','Brandenburg'] },
  { code: 'TR', name: 'Turkey', phone: '+90', flag: '🇹🇷', states: ['Istanbul','Ankara','Izmir','Antalya','Bursa','Adana','Konya','Gaziantep','Mersin','Kayseri'] },
].sort((a, b) => a.name.localeCompare(b.name));

export default function BranchManagement() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    branch_code: '',
    region_id: '',
    area_id: '',
    brand_id: '',
    warehouse_id: '',
    address: '',
    country: '',
    state: '',
    city: '',
    postal_code: '',
    phone: '',
    location_instructions: '',
    latitude: '',
    longitude: '',
    timezone: '',
    operating_hours: [
      { day: 'mon', closed: false, twenty_four_hours: false, periods: [{ open: '09:00', close: '18:00' }] },
      { day: 'tue', closed: false, twenty_four_hours: false, periods: [{ open: '09:00', close: '18:00' }] },
      { day: 'wed', closed: false, twenty_four_hours: false, periods: [{ open: '09:00', close: '18:00' }] },
      { day: 'thu', closed: false, twenty_four_hours: false, periods: [{ open: '09:00', close: '18:00' }] },
      { day: 'fri', closed: false, twenty_four_hours: false, periods: [{ open: '09:00', close: '18:00' }] },
      { day: 'sat', closed: false, twenty_four_hours: false, periods: [{ open: '10:00', close: '17:00' }] },
      { day: 'sun', closed: true,  twenty_four_hours: false, periods: [] },
    ],
    // Mystery Shopping Branch Classification (Aug 2026). One of:
    //   'client' | 'competitor' | 'system'
    // Required on every create and required to save any legacy
    // unclassified row on edit.
    branch_classification: '',
    ownership_type: '',
    // Feb 2026 — competitor branches only. Required text (server-side)
    // when branch_classification === 'competitor'. Ignored otherwise.
    competitor_brand: '',
    status: 'active',
  });
  const [codeError, setCodeError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  // Phase 4B — branch-level survey access (POS store code + QR/Numeric).
  const [accessProfiles, reloadAccessProfiles] = useAccessProfiles();
  const [branchAccess, setBranchAccess] = useState(EMPTY_BRANCH_ACCESS);
  const [branchAccessInitial, setBranchAccessInitial] = useState(EMPTY_BRANCH_ACCESS);
  // Survey access is only validated/saved when the user actually changed it.
  const accessTouched = JSON.stringify(branchAccess) !== JSON.stringify(branchAccessInitial);
  // Feb 2026 — Supported Countries master + geographic subdivisions.
  const [supportedCountries, setSupportedCountries] = useState([]); // [{iso_code,name,status}]
  const [subdivisions, setSubdivisions] = useState([]);             // [{code,name,type}]
  const [areas, setAreas] = useState([]);
  // Feb 2026 — postal-code lookup state.
  const [postalBusy, setPostalBusy] = useState(false);
  const [postalCandidates, setPostalCandidates] = useState(null); // null=idle; [] = no match
  const [postalNoMatch, setPostalNoMatch] = useState(false);
  const [tzList] = useState(() => {
    try {
      return typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
    } catch { return []; }
  });
  // Phase 2 — location licensing state.
  const [usage, setUsage] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  // Sept 2026 — two-step add-Location dialog. `count` shows the quantity
  // picker; `review` shows the Stripe-priced review with an explicit
  // "Confirm and pay" button. Entitlement is still webhook-gated on
  // the backend — this is customer-facing consent surfacing.
  const [addStep, setAddStep] = useState('count'); // 'count' | 'review'
  const [addPreview, setAddPreview] = useState(null);
  const [addCount, setAddCount] = useState(1);
  const [addBusy, setAddBusy] = useState(false);

  // Auto-suggest a branch code: first 3 letters of name (uppercase A–Z) + 4
  // random alphanumeric chars. Used only when creating and the user hasn't
  // typed a code themselves.
  const suggestBranchCode = (rawName) => {
    const letters = (rawName || '').replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase().padEnd(3, 'X');
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let suffix = '';
    for (let i = 0; i < 4; i++) {
      suffix += charset[Math.floor(Math.random() * charset.length)];
    }
    return `${letters}${suffix}`;
  };

  const selectedCountry = supportedCountries.find(c => c.iso_code === formData.country);
  const stateOptions = subdivisions;

  useEffect(() => {
    fetchBranches();
    fetchRegions();
    fetchUsage();
    fetchSupportedCountries();
    fetchAreas();
  }, []);

  // Load subdivisions from the open ISO 3166-2 dataset whenever the
  // Country changes. Never runs for the legacy hard-coded list.
  useEffect(() => {
    (async () => {
      const iso = (formData.country || '').toUpperCase();
      if (!iso || iso.length !== 2) { setSubdivisions([]); return; }
      try {
        const r = await fetch(`${API}/geo/subdivisions/${iso}`, { credentials: 'include' });
        if (!r.ok) { setSubdivisions([]); return; }
        const data = await r.json();
        setSubdivisions(data.rows || []);
      } catch { setSubdivisions([]); }
    })();
  }, [formData.country]);

  const fetchSupportedCountries = async () => {
    try {
      const r = await fetch(`${API}/platform/supported-countries`, { credentials: 'include' });
      if (!r.ok) return;
      const data = await r.json();
      // Only OPEN + WAITLIST are selectable for NEW branches. Unavailable
      // countries are excluded from the dropdown but historical rows keep
      // their stored value.
      setSupportedCountries((data.rows || []).filter(r => r.status !== 'unavailable'));
    } catch (e) { /* non-fatal */ }
  };

  const fetchAreas = async () => {
    try {
      const r = await fetch(`${API}/areas`, { credentials: 'include' });
      if (!r.ok) return;
      const data = await r.json();
      setAreas(Array.isArray(data) ? data : []);
    } catch (e) { /* non-fatal */ }
  };

  // Postal-code lookup (Zippopotam-backed cache on the server).
  const runPostalLookup = async (country, postal) => {
    setPostalCandidates(null);
    setPostalNoMatch(false);
    if (!country || !(postal || '').trim()) return;
    setPostalBusy(true);
    try {
      const q = new URLSearchParams({ country, postal_code: postal.trim() });
      const r = await fetch(`${API}/geo/postal-lookup?${q}`, { credentials: 'include' });
      if (!r.ok) { setPostalNoMatch(true); return; }
      const data = await r.json();
      const rows = data.rows || [];
      if (!rows.length) { setPostalNoMatch(true); return; }
      if (rows.length === 1) {
        applyPostalCandidate(rows[0]);
      } else {
        setPostalCandidates(rows);
      }
    } catch (e) {
      setPostalNoMatch(true);
    } finally {
      setPostalBusy(false);
    }
  };

  const applyPostalCandidate = (row) => {
    setFormData((prev) => ({
      ...prev,
      city: row.city || prev.city,
      state: row.state || prev.state,
      // Auto-position the map pin when the lookup returned lat/lng.
      latitude: (Number.isFinite(row.latitude) ? row.latitude : prev.latitude),
      longitude: (Number.isFinite(row.longitude) ? row.longitude : prev.longitude),
    }));
    setPostalCandidates(null);
    setPostalNoMatch(false);
  };

  const fetchUsage = async () => {
    try {
      const response = await fetch(`${API}/subscriptions/location-usage`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      // Non-fatal — page still works without the counter (e.g. platform_admin
      // impersonating a tenant without a subscription).
      console.error('Error fetching location usage:', error);
    }
  };

  const loadAddLocationsPreview = async () => {
    if (!addCount || addCount < 1) return;
    setAddBusy(true);
    try {
      const r = await fetch(`${API}/subscriptions/add-locations/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ additional: Number(addCount) }),
      });
      if (r.ok) {
        setAddPreview(await r.json());
        setAddStep('review');
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Unable to preview the purchase. Please try again.');
      }
    } catch { toast.error('Network error. Please try again.'); }
    finally { setAddBusy(false); }
  };

  const handleAddLocations = async () => {
    if (!addCount || addCount < 1) return;
    setAddBusy(true);
    try {
      const response = await fetch(`${API}/subscriptions/add-locations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ additional: Number(addCount) }),
      });
      if (response.ok) {
        // Backend now only returns 200 once Stripe confirms the
        // proration invoice is paid — safe to advertise the new
        // entitlement immediately.
        toast.success(`${addCount} Location${addCount > 1 ? 's' : ''} paid & added — you can now create the Branch.`);
        setAddOpen(false);
        setAddCount(1);
        fetchUsage();
      } else if (response.status === 402) {
        // Card declined / invoice unpaid — entitlement was NOT
        // bumped. Surface the Stripe message and keep the dialog open
        // so the user can retry after updating their payment method.
        const err = await response.json().catch(() => ({}));
        toast.error(
          err.detail || 'Payment for the additional Locations was not completed. Update your payment method and try again.',
          { duration: 8000 },
        );
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add Locations');
      }
    } catch (error) {
      console.error('Error adding locations:', error);
      toast.error('Failed to add Locations');
    } finally {
      setAddBusy(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await fetch(`${API}/branches`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await fetch(`${API}/regions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRegions(data);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCodeError('');

    try {
      const url = isEditing
        ? `${API}/branches/${selectedBranch.branch_id}`
        : `${API}/branches`;

      // Trim/normalise branch_code; empty string → omit.
      const payload = { ...formData };
      payload.branch_code = (payload.branch_code || '').trim() || null;
      // Coerce numeric geo fields.
      payload.latitude  = payload.latitude  === '' ? null : Number(payload.latitude);
      payload.longitude = payload.longitude === '' ? null : Number(payload.longitude);
      if (Number.isNaN(payload.latitude))  payload.latitude  = null;
      if (Number.isNaN(payload.longitude)) payload.longitude = null;
      // Empty optional strings → null so BC clears cleanly.
      ['area_id','brand_id','warehouse_id','postal_code','location_instructions','timezone','ownership_type'].forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      // Feb 2026 — competitor_brand only applies to competitor rows. For
      // other classifications, drop the key so the server clears any
      // stale value on the record.
      if (payload.branch_classification === 'competitor') {
        payload.competitor_brand = (payload.competitor_brand || '').trim();
      } else {
        payload.competitor_brand = null;
      }

      // Client-side guard — the server will reject too, but this saves
      // the user a round-trip when they leave the classification blank.
      if (!payload.branch_classification) {
        toast.error('Please select a Branch Classification (Client / Competitor / System).');
        return;
      }
      const accessErr = accessTouched ? branchAccessError(accessProfiles, branchAccess, selectedBranch?.branch_id) : '';
      if (accessErr) {
        toast.error(accessErr);
        return;
      }

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const saved = await response.json().catch(() => ({}));
        const accessFail = saved.branch_id && accessTouched ? await saveBranchAccess(accessProfiles, saved.branch_id, branchAccess) : null;
        if (accessFail) toast.error(`Branch saved, but survey access wasn't: ${accessFail}`);
        else toast.success(isEditing ? 'Branch updated successfully' : 'Branch created successfully');
        reloadAccessProfiles();
        setDialogOpen(false);
        resetForm();
        fetchBranches();
        fetchUsage();
      } else if (response.status === 402) {
        // Phase 2 — location licensing gate. Structured detail from the
        // backend tells the buyer how many licences they've used and
        // opens the "Add locations" flow.
        const err = await response.json().catch(() => ({}));
        const d = err.detail || {};
        toast.error(d.message || 'Add locations to your subscription to continue.', {
          action: { label: 'Add locations', onClick: () => setAddOpen(true) },
        });
      } else {
        const error = await response.json().catch(() => ({}));
        const detail = error.detail || 'Failed to save branch';
        if (/branch code/i.test(detail)) {
          setCodeError(detail);
        } else {
          toast.error(detail);
        }
      }
    } catch (error) {
      console.error('Error saving branch:', error);
      toast.error('Failed to save branch');
    }
  };

  const handleEdit = (branch) => {
    setSelectedBranch(branch);
    setFormData({
      name: branch.name,
      branch_code: branch.branch_code || '',
      region_id: branch.region_id || '',
      area_id: branch.area_id || '',
      brand_id: branch.brand_id || '',
      warehouse_id: branch.warehouse_id || '',
      address: branch.address || '',
      country: branch.country || '',
      state: branch.state || '',
      city: branch.city || '',
      postal_code: branch.postal_code || '',
      phone: branch.phone || '',
      location_instructions: branch.location_instructions || '',
      latitude: branch.latitude ?? '',
      longitude: branch.longitude ?? '',
      timezone: branch.timezone || '',
      operating_hours: (branch.operating_hours && branch.operating_hours.length)
        ? branch.operating_hours
        : formData.operating_hours,
      // Empty string = "Unclassified"; the form forces a selection before save.
      branch_classification: branch.branch_classification || '',
      ownership_type: branch.ownership_type || '',
      competitor_brand: branch.competitor_brand || '',
      // Deactivated rows may still carry a stale status='active'.
      status: branch.is_active === false
        ? (branch.status && branch.status !== 'active' ? branch.status : 'closed')
        : (branch.status || 'active'),
    });
    const access = branchAccessFor(accessProfiles, branch.branch_id);
    setBranchAccess(access);
    setBranchAccessInitial(access);
    setCodeError('');
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleDeactivate = async (branchId) => {
    if (!confirm('Are you sure you want to deactivate this branch?')) return;
    
    try {
      const response = await fetch(`${API}/branches/${branchId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Branch deactivated');
        fetchBranches();
      } else {
        toast.error('Failed to deactivate branch');
      }
    } catch (error) {
      console.error('Error deactivating branch:', error);
      toast.error('Failed to deactivate branch');
    }
  };

  const handleReactivate = async (branch) => {
    try {
      const response = await fetch(`${API}/branches/${branch.branch_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'active' }),
      });
      if (response.ok) {
        toast.success('Branch reactivated');
        fetchBranches();
        fetchUsage();
        return;
      }
      const err = await response.json().catch(() => ({}));
      const d = err.detail;
      if (response.status === 402) {
        toast.error((d && d.message) || 'Add locations to your subscription to continue.', {
          action: { label: 'Add locations', onClick: () => setAddOpen(true) },
        });
      } else {
        toast.error(typeof d === 'string' ? d : 'Failed to reactivate branch');
      }
    } catch (error) {
      toast.error('Failed to reactivate branch');
    }
  };

  // Legacy rows may lack address fields; on edit only already-filled ones stay required (they can't be cleared).
  const mustFill = (field) => !isEditing || !!String(selectedBranch?.[field] ?? '').trim();

  const handleRegenerateQR = async (branch) => {
    try {
      const response = await fetch(`${API}/branches/${branch.branch_id}/regenerate-qr`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedBranch({ ...branch, qr_code: data.qr_code });
        toast.success('QR code regenerated');
        fetchBranches();
      } else {
        toast.error('Failed to regenerate QR code');
      }
    } catch (error) {
      console.error('Error regenerating QR:', error);
      toast.error('Failed to regenerate QR code');
    }
  };

  const handleViewQR = (branch) => {
    setSelectedBranch(branch);
    setQrDialogOpen(true);
  };

  const downloadQRCode = () => {
    if (!selectedBranch?.qr_code) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${selectedBranch.qr_code}`;
    link.download = `qr-${selectedBranch.name.replace(/\s+/g, '-').toLowerCase()}.png`;
    link.click();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      branch_code: '',
      region_id: '',
      area_id: '',
      brand_id: '',
      warehouse_id: '',
      address: '',
      country: '',
      state: '',
      city: '',
      postal_code: '',
      phone: '',
      location_instructions: '',
      latitude: '',
      longitude: '',
      timezone: '',
      operating_hours: [
        { day: 'mon', closed: false, twenty_four_hours: false, periods: [{ open: '09:00', close: '18:00' }] },
        { day: 'tue', closed: false, twenty_four_hours: false, periods: [{ open: '09:00', close: '18:00' }] },
        { day: 'wed', closed: false, twenty_four_hours: false, periods: [{ open: '09:00', close: '18:00' }] },
        { day: 'thu', closed: false, twenty_four_hours: false, periods: [{ open: '09:00', close: '18:00' }] },
        { day: 'fri', closed: false, twenty_four_hours: false, periods: [{ open: '09:00', close: '18:00' }] },
        { day: 'sat', closed: false, twenty_four_hours: false, periods: [{ open: '10:00', close: '17:00' }] },
        { day: 'sun', closed: true,  twenty_four_hours: false, periods: [] },
      ],
      branch_classification: '',
      ownership_type: '',
      competitor_brand: '',
      status: 'active',
    });
    setBranchAccess(EMPTY_BRANCH_ACCESS);
    setBranchAccessInitial(EMPTY_BRANCH_ACCESS);
    setCodeError('');
    setIsEditing(false);
    setSelectedBranch(null);
    setPostalCandidates(null);
    setPostalNoMatch(false);
  };

  // Aug 2026 — filter branch list by classification.
  const [classificationFilter, setClassificationFilter] = useState('all');
  const filteredBranches = branches.filter(b => {
    if (classificationFilter === 'all') return true;
    if (classificationFilter === 'unclassified') return !b.branch_classification;
    return b.branch_classification === classificationFilter;
  });

  // Aug 2026 — allow platform_admin to manage branches when impersonating a
  // tenant. Without this, the Add Branch dialog, classification filter chips,
  // and Edit actions are hidden from the internal team who need to backfill
  // branch_classification on legacy branches.
  const canEditBranches =
    user?.role === 'tenant_admin' ||
    user?.role === 'regional_manager' ||
    user?.role === 'platform_admin';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="branches-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Montserrat']" data-testid="branches-title">
              Branch Management
            </h1>
            <p className="text-[#64748B] mt-1">
              Manage your branches and QR codes
            </p>
          </div>

          {canEditBranches && (
            <div className="flex items-center gap-3">
              {usage?.totals?.purchased > 0 && (
                <span
                  data-testid="location-usage-counter"
                  className={`text-xs px-3 py-1.5 rounded-full font-medium ${
                    usage.totals.available === 0
                      ? 'bg-rose-50 text-rose-700 border border-rose-200'
                      : 'bg-slate-50 text-slate-700 border border-slate-200'
                  }`}
                >
                  {usage.totals.counted} of {usage.totals.purchased} locations licensed
                  {usage.totals.available === 0 && ' — at capacity'}
                </span>
              )}
              {usage?.totals?.purchased > 0 && (
                <Dialog open={addOpen} onOpenChange={(open) => {
                  setAddOpen(open);
                  if (!open) { setAddStep('count'); setAddPreview(null); }
                }}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-[#1A3E6F] text-[#1A3E6F] font-['Montserrat'] font-semibold"
                      data-testid="add-locations-btn"
                    >
                      <Plus className="w-4 h-4 mr-1.5" /> Add locations
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[440px]">
                    {/* Step 1 — quantity picker */}
                    {addStep === 'count' && (
                      <>
                        <DialogHeader>
                          <DialogTitle className="font-['Montserrat']">Add locations</DialogTitle>
                          <DialogDescription>
                            Choose how many additional Locations you want to add. You'll review the exact amount due before paying.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 mt-2">
                          <Label htmlFor="add-locations-count">How many locations to add?</Label>
                          <Input
                            id="add-locations-count"
                            type="number"
                            min={1}
                            value={addCount}
                            onChange={(e) => setAddCount(Number(e.target.value) || 1)}
                            data-testid="add-locations-count"
                          />
                        </div>
                        <DialogFooter className="mt-4">
                          <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={addBusy}>Cancel</Button>
                          <Button
                            onClick={loadAddLocationsPreview}
                            disabled={addBusy || addCount < 1}
                            className="bg-[#1A3E6F] hover:bg-[#122c50] text-white"
                            data-testid="add-locations-review-btn"
                          >
                            {addBusy ? 'Loading…' : 'Review'}
                          </Button>
                        </DialogFooter>
                      </>
                    )}

                    {/* Step 2 — Confirm and pay review */}
                    {addStep === 'review' && addPreview && (
                      <>
                        <DialogHeader>
                          <DialogTitle className="font-['Montserrat']">Confirm your purchase</DialogTitle>
                          <DialogDescription>
                            Please review before we charge your saved payment method.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4 space-y-2 mt-2 text-sm" data-testid="add-locations-review">
                          <ReviewRow label="Product" value={addPreview.product || 'VOX subscription'} />
                          <ReviewRow label="Billing cycle" value={addPreview.billing_cycle} />
                          <ReviewRow label="Additional Locations" value={`+${addPreview.additional}`} />
                          <ReviewRow label="Total Locations after purchase" value={addPreview.new_quantity} />
                          <ReviewRow label="Price per Location" value={addPreview.unit_amount_display} />
                          <div className="border-t border-[#E2E8F0] pt-2 mt-2">
                            <div className="flex justify-between items-baseline">
                              <span className="text-[#0F172A] font-semibold">Amount due now</span>
                              <span className="text-[#0F172A] font-bold text-lg" data-testid="add-locations-amount-due">
                                {addPreview.amount_due_now_display}
                              </span>
                            </div>
                            <p className="text-[11px] text-[#64748B] mt-1">
                              Prorated for the remaining {addPreview.billing_cycle === 'annual' ? 'annual' : 'monthly'} term. Your renewal date does not change.
                            </p>
                          </div>
                        </div>
                        <DialogFooter className="mt-4 gap-2">
                          <Button variant="ghost" onClick={() => setAddStep('count')} disabled={addBusy} data-testid="add-locations-back-btn">Back</Button>
                          <Button
                            onClick={handleAddLocations}
                            disabled={addBusy}
                            className="bg-[#1A3E6F] hover:bg-[#122c50] text-white"
                            data-testid="add-locations-confirm-pay"
                          >
                            {addBusy ? 'Processing…' : 'Confirm and pay'}
                          </Button>
                        </DialogFooter>
                      </>
                    )}
                  </DialogContent>
                </Dialog>
              )}
              {/* Phase B — the Add Branch trigger is disabled visually
                  when capacity is exhausted. The `onOpenChange` guard
                  below is the belt-and-braces server-safe block; this
                  disabled state prevents the user from even attempting
                  the click when they can see a clear "capacity full"
                  affordance next to a live "Add locations" CTA.
                  Rendered whenever `available <= 0` and the tenant
                  already has at least one branch — covers both fresh
                  paid tenants at capacity AND grandfathered tenants
                  with legacy branches but `purchased = 0`. */}
              {/* The Dialog stays mounted at full capacity so Edit keeps working; only the Add trigger is swapped. */}
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                // Phase B — pre-gate: block *entering* the Create Branch
                // flow when the tenant has no available Locations. Do
                // NOT block editing existing branches (isEditing = true)
                // — this only affects the "Add Branch" entry point.
                if (open && !isEditing && (usage?.totals?.available ?? 0) <= 0) {
                  toast.error(
                    'You have used all of your purchased Locations. Add a Location to create another Branch.',
                    { action: { label: 'Add locations', onClick: () => setAddOpen(true) } },
                  );
                  return;
                }
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                {(usage?.totals?.available ?? 0) <= 0 && (usage?.totals?.counted ?? 0) > 0 ? (
                  <Button
                    variant="outline"
                    onClick={() => setAddOpen(true)}
                    className="border-rose-300 text-rose-700 hover:bg-rose-50 font-['Montserrat'] font-semibold"
                    data-testid="add-branch-blocked-btn"
                    title="You have used all of your purchased Locations. Add a Location to create another Branch."
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Location to create Branch
                  </Button>
                ) : (
                <DialogTrigger asChild>
                  <Button
                    className="bg-[#1A3E6F] hover:bg-[#122c50] text-white font-['Montserrat'] font-semibold"
                    data-testid="add-branch-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Branch
                  </Button>
                </DialogTrigger>
                )}
                <DialogContent
                  className="max-w-3xl w-[95vw] p-0 gap-0 max-h-[92vh] flex flex-col"
                  data-testid="branch-dialog"
                >
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#E2E8F0] shrink-0">
                    <DialogTitle className="font-['Montserrat'] text-[#0F172A]">
                      {isEditing ? 'Edit Branch' : 'Add New Branch'}
                    </DialogTitle>
                    <DialogDescription className="text-[#64748B]">
                      {isEditing
                        ? 'Update branch details, including its classification (Client / Competitor / System).'
                        : 'Register a new branch. Country availability is enforced by the platform Supported Countries list.'}
                    </DialogDescription>
                  </DialogHeader>
                  <form
                    onSubmit={handleSubmit}
                    className="flex-1 flex flex-col overflow-hidden"
                    data-testid="branch-form"
                  >
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6" data-testid="branch-form-scroll">
                      {/* ─── A. Branch Identity ─────────────────────── */}
                      <section className="space-y-3">
                        <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1A3E6F]">A. Branch Identity</h3>
                        <div className="space-y-2">
                          <Label htmlFor="name">Branch Name *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            onBlur={() => {
                              if (!isEditing && !formData.branch_code) {
                                setFormData(prev => ({ ...prev, branch_code: suggestBranchCode(prev.name) }));
                              }
                            }}
                            placeholder="e.g., Downtown Location"
                            required
                            data-testid="branch-name-input"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="branch_code">Branch Code <span className="text-[#64748B] font-normal">(auto-generated)</span></Label>
                            <Input
                              id="branch_code"
                              value={formData.branch_code}
                              onChange={(e) => {
                                const v = (e.target.value || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 20).toUpperCase();
                                setFormData({ ...formData, branch_code: v });
                                if (codeError) setCodeError('');
                              }}
                              placeholder="Leave blank to auto-generate"
                              maxLength={20}
                              data-testid="branch-code-input"
                            />
                            {codeError && <p className="text-xs text-[#D62B27]" data-testid="branch-code-error">{codeError}</p>}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="branch_classification">Branch Classification <span className="text-[#D62B27]">*</span></Label>
                            <select
                              id="branch_classification"
                              value={formData.branch_classification}
                              onChange={(e) => setFormData({ ...formData, branch_classification: e.target.value })}
                              required
                              data-testid="branch-classification-select"
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              <option value="" disabled>
                                {isEditing && !selectedBranch?.branch_classification ? 'Unclassified — select one' : 'Select classification…'}
                              </option>
                              <option value="client">Client Branch</option>
                              <option value="competitor">Competitor Branch</option>
                              <option value="system">System / Comparison Branch</option>
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="ownership_type">Ownership Type</Label>
                            <select
                              id="ownership_type"
                              value={formData.ownership_type}
                              onChange={(e) => setFormData({ ...formData, ownership_type: e.target.value })}
                              data-testid="branch-ownership-select"
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              <option value="">— Select —</option>
                              <option value="company_owned">Company-owned</option>
                              <option value="franchise">Franchise</option>
                              <option value="licensee">Licensee</option>
                              <option value="other">Other</option>
                              <option value="unknown">Unknown</option>
                            </select>
                            <p className="text-[11px] text-[#64748B]">Independent from Branch Classification.</p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="brand_id">Brand <span className="text-[#64748B] font-normal">(optional)</span></Label>
                            <Input
                              id="brand_id"
                              value={formData.brand_id}
                              onChange={(e) => setFormData({ ...formData, brand_id: e.target.value })}
                              placeholder="Brand identifier or name"
                              data-testid="branch-brand-input"
                            />
                          </div>
                        </div>
                        {formData.branch_classification === 'competitor' && (
                          <div className="space-y-2" data-testid="branch-competitor-brand-group">
                            <Label htmlFor="competitor_brand">Competitor Brand <span className="text-[#D62B27]">*</span></Label>
                            <Input
                              id="competitor_brand"
                              value={formData.competitor_brand}
                              onChange={(e) => setFormData({ ...formData, competitor_brand: e.target.value })}
                              required
                              maxLength={80}
                              placeholder="e.g. Ippudo, Ramen Kagetsu"
                              data-testid="branch-competitor-brand-input"
                            />
                          </div>
                        )}
                      </section>

                      {formData.branch_classification !== 'competitor' && (
                        <BranchAccessFields profiles={accessProfiles} value={branchAccess} onChange={setBranchAccess} branchId={selectedBranch?.branch_id} />
                      )}

                      {/* ─── B. Geographic Location ─────────────────── */}
                      <section className="space-y-3">
                        <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1A3E6F]">B. Geographic Location</h3>
                        {/* Row 1: Country */}
                        <div className="space-y-2">
                          <Label>Country <span className="text-[#D62B27]">*</span></Label>
                          <Select
                            value={formData.country || 'none'}
                            onValueChange={(value) => setFormData({ ...formData, country: value === 'none' ? '' : value, state: '', city: '', postal_code: '' })}
                          >
                            <SelectTrigger data-testid="branch-country-select">
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              <SelectItem value="none">Select country</SelectItem>
                              {supportedCountries.map((c) => (
                                <SelectItem key={c.iso_code} value={c.iso_code}>
                                  {c.name}{c.status === 'waitlist' ? ' (waitlist)' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-[11px] text-[#64748B]">Only Supported Countries are listed. Historical rows keep their stored value.</p>
                        </div>
                        {/* Row 2: Postal Code (with lookup) */}
                        <div className="space-y-2">
                          <Label htmlFor="postal_code">Postal / ZIP / PIN Code <span className="text-[#D62B27]">*</span></Label>
                          <div className="flex gap-2">
                            <Input
                              id="postal_code"
                              value={formData.postal_code}
                              onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                              onBlur={() => runPostalLookup(formData.country, formData.postal_code)}
                              placeholder={formData.country ? "e.g. 4026 · SW1A 1AA · 10001" : "Select country first"}
                              disabled={!formData.country}
                              required={mustFill('postal_code')}
                              maxLength={12}
                              data-testid="branch-postal-input"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!formData.country || !formData.postal_code || postalBusy}
                              onClick={() => runPostalLookup(formData.country, formData.postal_code)}
                              data-testid="branch-postal-lookup-btn"
                            >
                              {postalBusy ? 'Looking up…' : 'Look up'}
                            </Button>
                          </div>
                          <p className="text-[11px] text-[#64748B]">Enter the postal code to automatically identify the city and state/province where available.</p>
                          {postalCandidates && postalCandidates.length > 1 && (
                            <div className="rounded-md border border-[#E2E8F0] p-2 space-y-1" data-testid="branch-postal-candidates">
                              <p className="text-[11px] font-semibold text-[#0F172A]">This postal code matches multiple localities — pick one:</p>
                              {postalCandidates.map((row, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  className="w-full text-left text-xs px-2 py-1 rounded hover:bg-[#F1F5F9] border border-transparent hover:border-[#CBD5E1]"
                                  onClick={() => applyPostalCandidate(row)}
                                  data-testid={`branch-postal-candidate-${idx}`}
                                >
                                  <span className="font-semibold">{row.city}</span>
                                  {row.state ? <span className="text-[#64748B]"> · {row.state}</span> : null}
                                </button>
                              ))}
                            </div>
                          )}
                          {postalNoMatch && (
                            <p className="text-[11px] text-[#B45309]" data-testid="branch-postal-no-match">
                              We couldn't auto-detect the state and city for this postal code. Please enter them manually below.
                            </p>
                          )}
                        </div>
                        {/* Row 3: State / Province */}
                        <div className="space-y-2">
                          <Label>State / Province / Territory <span className="text-[#D62B27]">*</span></Label>
                          {stateOptions.length > 0 ? (
                            <Select
                              value={formData.state || 'none'}
                              onValueChange={(value) => setFormData({ ...formData, state: value === 'none' ? '' : value })}
                              disabled={!formData.country}
                            >
                              <SelectTrigger data-testid="branch-state-select">
                                <SelectValue placeholder={formData.country ? "Select subdivision" : "Select country first"} />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                <SelectItem value="none">— None —</SelectItem>
                                {stateOptions.map((s) => (
                                  <SelectItem key={s.code} value={s.name}>{s.name}<span className="text-[#94A3B8] text-xs ml-1">({s.type})</span></SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={formData.state}
                              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                              placeholder={formData.country ? "Enter state / province" : "Select country first"}
                              disabled={!formData.country}
                              required={mustFill('state')}
                              data-testid="branch-state-input"
                            />
                          )}
                        </div>
                        {/* Row 4: City / Municipality */}
                        <div className="space-y-2">
                          <Label htmlFor="city">City / Municipality <span className="text-[#D62B27]">*</span></Label>
                          <Input
                            id="city"
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            placeholder="City / Municipality"
                            required={mustFill('city')}
                            data-testid="branch-city-input"
                          />
                        </div>
                        {/* Row 5: Full Address */}
                        <div className="space-y-2">
                          <Label htmlFor="address">Full Address <span className="text-[#D62B27]">*</span></Label>
                          <Input
                            id="address"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            placeholder="Street, building, unit, barangay/district"
                            required={mustFill('address')}
                            data-testid="branch-address-input"
                          />
                        </div>
                        {/* Row 6: Phone */}
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone <span className="text-[#64748B] font-normal">(optional)</span></Label>
                          <Input
                            id="phone"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="e.g. +63 917 555 1234"
                            data-testid="branch-phone-input"
                          />
                        </div>
                        {/* Row 7: Location Instructions */}
                        <div className="space-y-2">
                          <Label htmlFor="location_instructions">Location Instructions <span className="text-[#64748B] font-normal">(optional)</span></Label>
                          <Input
                            id="location_instructions"
                            value={formData.location_instructions}
                            onChange={(e) => setFormData({ ...formData, location_instructions: e.target.value })}
                            placeholder="e.g. Level 2 beside the cinema · Terminal 3 after security"
                            data-testid="branch-location-instructions-input"
                          />
                        </div>
                      </section>

                      {/* ─── C. GPS + Map ───────────────────────────── */}
                      <section className="space-y-3">
                        <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1A3E6F]">C. GPS + Map</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="latitude">Latitude</Label>
                            <Input
                              id="latitude"
                              value={formData.latitude}
                              onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                              placeholder="e.g. 14.312213"
                              inputMode="decimal"
                              data-testid="branch-latitude-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="longitude">Longitude</Label>
                            <Input
                              id="longitude"
                              value={formData.longitude}
                              onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                              placeholder="e.g. 121.111412"
                              inputMode="decimal"
                              data-testid="branch-longitude-input"
                            />
                          </div>
                        </div>
                        <BranchMapPicker
                          lat={formData.latitude}
                          lng={formData.longitude}
                          onChange={({ lat, lng }) => setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }))}
                        />
                        <p className="text-[11px] text-[#64748B]">Click on the map to drop a pin, or drag the marker to fine-tune. Coordinates are saved on the branch record.</p>
                      </section>

                      {/* ─── D. Client Organisation ─────────────────── */}
                      <section className="space-y-3">
                        <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1A3E6F]">D. Client Organisation</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="area_id">Area <span className="text-[#64748B] font-normal">(optional)</span></Label>
                            <select
                              id="area_id"
                              value={formData.area_id || ''}
                              onChange={(e) => setFormData({ ...formData, area_id: e.target.value })}
                              data-testid="branch-area-select"
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              <option value="">— No Area —</option>
                              {areas.map(a => (
                                <option key={a.area_id} value={a.area_id}>{a.name}</option>
                              ))}
                            </select>
                            <p className="text-[11px] text-[#64748B]">Client-defined grouping. Independent from geographic State/Province.</p>
                          </div>
                          {regions.length > 0 && (
                            <div className="space-y-2">
                              <Label htmlFor="region_id">Region <span className="text-[#64748B] font-normal">(legacy)</span></Label>
                              <select
                                id="region_id"
                                value={formData.region_id || ''}
                                onChange={(e) => setFormData({ ...formData, region_id: e.target.value })}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                data-testid="branch-region-select"
                              >
                                <option value="">— None —</option>
                                {regions.map(r => (
                                  <option key={r.region_id} value={r.region_id}>{r.name}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </section>

                      {/* ─── E. Operating Hours ─────────────────────── */}
                      <section className="space-y-3">
                        <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1A3E6F]">E. Operating Hours</h3>
                        <div className="space-y-2">
                          <Label htmlFor="timezone">Timezone <span className="text-[#64748B] font-normal">(local)</span></Label>
                          <select
                            id="timezone"
                            value={formData.timezone || ''}
                            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                            data-testid="branch-timezone-select"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            <option value="">— Select timezone —</option>
                            {tzList.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                          </select>
                        </div>
                        <div className="space-y-2 rounded-md border border-[#E2E8F0] overflow-hidden">
                          {formData.operating_hours.map((oh, idx) => (
                            <div key={oh.day} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b border-[#F1F5F9] last:border-0" data-testid={`ophours-${oh.day}`}>
                              <div className="col-span-2 text-xs font-semibold uppercase text-[#0F172A]">{oh.day}</div>
                              <label className="col-span-2 flex items-center gap-1.5 text-xs">
                                <input type="checkbox" checked={oh.closed} onChange={(e) => {
                                  const next = [...formData.operating_hours];
                                  next[idx] = { ...next[idx], closed: e.target.checked, twenty_four_hours: false, periods: e.target.checked ? [] : (next[idx].periods.length ? next[idx].periods : [{ open: '09:00', close: '18:00' }]) };
                                  setFormData({ ...formData, operating_hours: next });
                                }} data-testid={`ophours-${oh.day}-closed`} />
                                Closed
                              </label>
                              <label className="col-span-2 flex items-center gap-1.5 text-xs">
                                <input type="checkbox" checked={oh.twenty_four_hours} onChange={(e) => {
                                  const next = [...formData.operating_hours];
                                  next[idx] = { ...next[idx], twenty_four_hours: e.target.checked, closed: false, periods: e.target.checked ? [] : (next[idx].periods.length ? next[idx].periods : [{ open: '09:00', close: '18:00' }]) };
                                  setFormData({ ...formData, operating_hours: next });
                                }} data-testid={`ophours-${oh.day}-24h`} />
                                24 h
                              </label>
                              <div className="col-span-6 space-y-1">
                                {!oh.closed && !oh.twenty_four_hours && (oh.periods || []).map((p, pidx) => (
                                  <div key={pidx} className="flex items-center gap-1.5 text-xs">
                                    <Input type="time" value={p.open} onChange={(e) => {
                                      const next = [...formData.operating_hours];
                                      const periods = [...next[idx].periods];
                                      periods[pidx] = { ...periods[pidx], open: e.target.value };
                                      next[idx] = { ...next[idx], periods };
                                      setFormData({ ...formData, operating_hours: next });
                                    }} className="h-8 w-28" data-testid={`ophours-${oh.day}-open-${pidx}`} />
                                    <span>–</span>
                                    <Input type="time" value={p.close} onChange={(e) => {
                                      const next = [...formData.operating_hours];
                                      const periods = [...next[idx].periods];
                                      periods[pidx] = { ...periods[pidx], close: e.target.value };
                                      next[idx] = { ...next[idx], periods };
                                      setFormData({ ...formData, operating_hours: next });
                                    }} className="h-8 w-28" data-testid={`ophours-${oh.day}-close-${pidx}`} />
                                    {(oh.periods.length > 1) && (
                                      <button type="button" className="text-[#D62B27] text-xs" onClick={() => {
                                        const next = [...formData.operating_hours];
                                        const periods = next[idx].periods.filter((_, i) => i !== pidx);
                                        next[idx] = { ...next[idx], periods };
                                        setFormData({ ...formData, operating_hours: next });
                                      }}>Remove</button>
                                    )}
                                  </div>
                                ))}
                                {!oh.closed && !oh.twenty_four_hours && (
                                  <button type="button" className="text-[#1A3E6F] text-xs font-semibold" onClick={() => {
                                    const next = [...formData.operating_hours];
                                    next[idx] = { ...next[idx], periods: [...(next[idx].periods || []), { open: '19:00', close: '22:00' }] };
                                    setFormData({ ...formData, operating_hours: next });
                                  }} data-testid={`ophours-${oh.day}-add-period`}>+ Add period</button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-[11px] text-[#64748B]">Multiple periods per day are supported (e.g. lunch + dinner service).</p>
                      </section>

                      {/* ─── F. Warehouse / Distribution Centre ─────── */}
                      <section className="space-y-3">
                        <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1A3E6F]">F. Warehouse / Distribution Centre <span className="text-[#64748B] font-normal">(optional)</span></h3>
                        <Input
                          value={formData.warehouse_id}
                          onChange={(e) => setFormData({ ...formData, warehouse_id: e.target.value })}
                          placeholder="Assigned warehouse / DC identifier"
                          data-testid="branch-warehouse-input"
                        />
                      </section>

                      {/* ─── G. Branch Status ───────────────────────── */}
                      <section className="space-y-3">
                        <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1A3E6F]">G. Branch Status</h3>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          data-testid="branch-status-select"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                          <option value="active">Active</option>
                          <option value="temp_closed">Temporarily Closed</option>
                          <option value="closed">Closed</option>
                        </select>
                        <p className="text-[11px] text-[#64748B]">Closed branches keep their historical FIND relationships.</p>
                      </section>
                    </div>

                    <DialogFooter className="px-6 py-4 border-t border-[#E2E8F0] shrink-0 bg-white">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="branch-cancel-btn">
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-[#1A3E6F] hover:bg-[#122c50]"
                        data-testid="save-branch-btn"
                      >
                        {isEditing ? 'Save Changes' : 'Create Branch'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
            </Dialog>
            </div>
          )}
        </div>

        {/* Aug 2026 — Branch Classification filter (internal roles only). */}
        {canEditBranches && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="branch-classification-filter">
            <span className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Filter:</span>
            {[
              { key: 'all',          label: 'All' },
              { key: 'client',       label: 'Client Branch' },
              { key: 'competitor',   label: 'Competitor Branch' },
              { key: 'system',       label: 'System / Comparison' },
              { key: 'unclassified', label: 'Unclassified' },
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setClassificationFilter(opt.key)}
                data-testid={`branch-filter-${opt.key}`}
                className={`px-3 py-1 text-xs font-semibold rounded-md border transition-colors ${
                  classificationFilter === opt.key
                    ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]'
                    : 'bg-white text-[#475569] border-[#CBD5E1] hover:border-[#94A3B8]'
                }`}
              >{opt.label}</button>
            ))}
          </div>
        )}

        {/* Branches Table */}
        <Card className="border-[#E2E8F0] shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="loading-spinner mx-auto mb-4"></div>
                <p className="text-[#64748B]">Loading branches...</p>
              </div>
            ) : filteredBranches.length === 0 ? (
              <div className="p-12 text-center">
                <Building2 className="w-12 h-12 text-[#B4B4B4] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A] font-['Montserrat']">
                  {branches.length === 0 ? 'No branches yet' : 'No branches match this filter'}
                </h3>
                <p className="text-[#64748B] mt-1 mb-4">
                  {branches.length === 0 ? 'Create your first branch to get started' : 'Try a different classification filter.'}
                </p>
                {canEditBranches && branches.length === 0 && (
                  <Button 
                    onClick={() => setDialogOpen(true)}
                    className="bg-[#1A3E6F] hover:bg-[#122c50]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Branch
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-['Montserrat']">Branch Name</TableHead>
                      <TableHead className="font-['Montserrat']">Classification</TableHead>
                      <TableHead className="font-['Montserrat']">Location</TableHead>
                      <TableHead className="font-['Montserrat']">Region</TableHead>
                      <TableHead className="font-['Montserrat']">Status</TableHead>
                      <TableHead className="font-['Montserrat']">QR Code</TableHead>
                      {canEditBranches && <TableHead className="font-['Montserrat']">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBranches.map((branch) => (
                      <TableRow key={branch.branch_id} data-testid={`branch-row-${branch.branch_id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#1A3E6F]/10 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-[#1A3E6F]" />
                            </div>
                            <div>
                              <p className="font-medium text-[#0F172A]" data-testid={`branch-name-${branch.branch_id}`}>
                                {branch.name}
                                {branch.branch_code && (
                                  <>
                                    <span className="text-[#94A3B8] mx-1.5">·</span>
                                    <span className="text-[#64748B] font-mono text-sm" data-testid={`branch-code-${branch.branch_id}`}>{branch.branch_code}</span>
                                  </>
                                )}
                              </p>
                              {branch.phone && (
                                <p className="text-xs text-[#64748B] flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {branch.phone}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell data-testid={`branch-classification-${branch.branch_id}`}>
                          {branch.branch_classification ? (
                            <span
                              className={
                                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ' +
                                (branch.branch_classification === 'client'
                                  ? 'bg-[#1A3E6F]/10 text-[#1A3E6F]'
                                  : branch.branch_classification === 'competitor'
                                  ? 'bg-[#D62B27]/10 text-[#D62B27]'
                                  : 'bg-[#64748B]/10 text-[#64748B]')
                              }
                            >
                              {{ client: 'Client', competitor: 'Competitor', system: 'System' }[branch.branch_classification]}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 text-xs font-medium">
                              Unclassified
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {branch.city || branch.state || branch.country ? (
                            <div className="flex items-center gap-1 text-[#64748B]">
                              <MapPin className="w-4 h-4 shrink-0" />
                              <span className="text-sm">
                                {[branch.city, branch.state, COUNTRIES.find(c => c.code === branch.country)?.name].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[#B4B4B4]">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {regions.find(r => r.region_id === branch.region_id)?.name || (
                            <span className="text-[#B4B4B4]">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`badge ${branch.is_active ? 'badge-active' : 'badge-expired'}`}>
                            {branch.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewQR(branch)}
                            className="border-[#5BC4BF] text-[#5BC4BF] hover:bg-[#5BC4BF]/10"
                            data-testid={`view-qr-${branch.branch_id}`}
                          >
                            <QrCode className="w-4 h-4 mr-1" />
                            View QR
                          </Button>
                        </TableCell>
                        {canEditBranches && (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(branch)}
                                className="text-[#64748B] hover:text-[#1A3E6F]"
                                data-testid={`edit-branch-${branch.branch_id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {branch.is_active && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeactivate(branch.branch_id)}
                                  className="text-[#64748B] hover:text-[#D62B27]"
                                  data-testid={`deactivate-branch-${branch.branch_id}`}
                                >
                                  <Power className="w-4 h-4" />
                                </Button>
                              )}
                              {!branch.is_active && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleReactivate(branch)}
                                  className="text-[#047857] border-[#A7F3D0] hover:bg-[#ECFDF5]"
                                  data-testid={`reactivate-branch-${branch.branch_id}`}
                                >
                                  <Power className="w-4 h-4 mr-1" /> Reactivate
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="font-['Montserrat'] text-[#0F172A]">
                QR Code - {selectedBranch?.name}
              </DialogTitle>
              <DialogDescription className="text-[#64748B]">
                Scan this QR code to open the branch survey.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center py-6">
              {selectedBranch?.qr_code ? (
                <>
                  <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-sm">
                    <img 
                      src={`data:image/png;base64,${selectedBranch.qr_code}`}
                      alt={`QR Code for ${selectedBranch.name}`}
                      className="w-48 h-48"
                      data-testid="qr-code-image"
                    />
                  </div>
                  <p className="text-sm text-[#64748B] mt-4 text-center">
                    Customers can scan this QR code to access the feedback survey
                  </p>
                  <div className="flex gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => handleRegenerateQR(selectedBranch)}
                      className="border-[#64748B]"
                      data-testid="regenerate-qr-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Regenerate
                    </Button>
                    <Button
                      onClick={downloadQRCode}
                      className="bg-[#1A3E6F] hover:bg-[#122c50]"
                      data-testid="download-qr-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center">
                  <QrCode className="w-12 h-12 text-[#B4B4B4] mx-auto mb-4" />
                  <p className="text-[#64748B]">No QR code available</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Areas section — group branches for Area Manager scoping */}
        <AreasSection branches={branches} />
      </div>
    </DashboardLayout>
  );
}

// Sept 2026 — small label/value row used inside the add-Location
// review card. Kept local to the file since it's only used here.
function ReviewRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-[#64748B]">{label}</span>
      <span className="text-[#0F172A] font-medium">{value}</span>
    </div>
  );
}

